import type {TenantBrandingPayload} from './tenantConfig'

/**
 * Runtime branding application for the Metronic-based supplier portal.
 *
 * Metronic is CSS-variable-driven: `var(--kt-primary)` (+ `-active`/`-light`/`-inverse`) is used across
 * the theme, so writing the tenant's brand color into those `:root` tokens re-skins the UI with no
 * component changes. We also set Bootstrap's `--bs-primary[-rgb]`. Logos are exposed via {@link brandLogo},
 * which takes the existing per-site fallback URL — so call sites are unchanged until a tenant config loads.
 */

let brandLogoLtr: string | null = null
let brandLogoRtl: string | null = null

type RGB = {r: number; g: number; b: number}

function parseHex(hex: string): RGB | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return null
  let h = m[1]
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  const int = parseInt(h, 16)
  return {r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255}
}

function toHex({r, g, b}: RGB): string {
  return '#' + [r, g, b].map((x) => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, '0')).join('')
}

/** Darken toward black by `amt` (0–1) — used for the `:active`/hover token. */
function darken(rgb: RGB, amt: number): RGB {
  return {r: rgb.r * (1 - amt), g: rgb.g * (1 - amt), b: rgb.b * (1 - amt)}
}

/** Lighten toward white by `amt` (0–1) — used for the light-background token. */
function tint(rgb: RGB, amt: number): RGB {
  return {r: rgb.r + (255 - rgb.r) * amt, g: rgb.g + (255 - rgb.g) * amt, b: rgb.b + (255 - rgb.b) * amt}
}

function setRootVar(name: string, value: string): void {
  if (typeof document !== 'undefined') document.documentElement.style.setProperty(name, value)
}

/** Write the tenant brand colors into the Metronic (`--kt-primary*`) + Bootstrap (`--bs-primary*`) tokens. */
export function applyBrandColors(colors: Record<string, string | null | undefined> | null | undefined): void {
  if (!colors) return
  const primary = colors.primary
  const rgb = typeof primary === 'string' ? parseHex(primary) : null
  if (primary && rgb) {
    setRootVar('--kt-primary', primary)
    setRootVar('--kt-primary-active', toHex(darken(rgb, 0.1)))
    setRootVar('--kt-primary-light', toHex(tint(rgb, 0.9)))
    setRootVar('--kt-primary-inverse', '#ffffff')
    setRootVar('--kt-primary-rgb', `${rgb.r},${rgb.g},${rgb.b}`)
    setRootVar('--bs-primary', primary)
    setRootVar('--bs-primary-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`)
  }
  const secondary = colors.secondary
  if (typeof secondary === 'string' && parseHex(secondary)) {
    setRootVar('--kt-secondary', secondary)
    setRootVar('--bs-secondary', secondary)
  }
  // Raw hex for any direct consumer (e.g. inline styles): --brand-<slot>.
  for (const [slot, value] of Object.entries(colors)) {
    if (typeof value === 'string' && value.trim()) setRootVar(`--brand-${slot.replace(/_/g, '-')}`, value)
  }
}

function applyFavicon(favicon: string | null | undefined): void {
  if (typeof document === 'undefined' || !favicon) return
  let link = document.querySelector<HTMLLinkElement>('link[rel="shortcut icon"], link[rel="icon"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  link.href = favicon
}

/** Stash the tenant's desktop logo URLs (per locale) for {@link brandLogo}. */
export function setBrandLogos(logos: NonNullable<TenantBrandingPayload['branding']>['logos']): void {
  brandLogoLtr = logos?.desktop?.en?.trim() || null
  brandLogoRtl = logos?.desktop?.ar?.trim() || brandLogoLtr
}

/**
 * The logo URL to render: the tenant's logo when a config is loaded, else the caller's existing
 * fallback (the bundled Metronic asset). So call sites behave exactly as before until a config arrives.
 */
export function brandLogo(isRtl: boolean, fallback: string): string {
  return (isRtl ? brandLogoRtl : brandLogoLtr) ?? fallback
}

/** Apply the full branding payload to the live document (colors, favicon, title, logos). */
export function applyBranding(config: TenantBrandingPayload): void {
  const name = config.tenant?.name
  if (typeof document !== 'undefined' && typeof name === 'string' && name.trim()) {
    document.title = `Supplier Panel | ${name}`
  }
  applyBrandColors(config.branding?.colors)
  applyFavicon(config.branding?.favicon)
  setBrandLogos(config.branding?.logos)
}
